/**
 * Keeps a wallet's private keys across the change of the signing message.
 *
 * Private wallet keys come from the wallet's signature over a fixed message,
 * so new wording would mean new keys. A wallet that already had a private
 * wallet signs the previous message once per device; that previous signature
 * is stored on the device, encrypted under the signature of the new message.
 * From then on only the new message is signed and its signature unlocks the
 * previous one, so balances, history and handles stay the same. A wallet with
 * no earlier private wallet uses the new signature directly.
 *
 * The stored record never holds anything usable without the wallet: the key
 * that opens it comes from the new message's signature, which only the
 * wallet can produce for this site, network and pool.
 */

export interface CarryOverScope {
  /** The network and pool the keys belong to, e.g. `evm:84532:0x…`. */
  scope: string;
  /** The wallet address, in the form its network compares (EVM: lowercase). */
  wallet: string;
}

export interface CarryOverCommitOptions {
  /** Cancel a pending save if its verified wallet session has changed. */
  isCurrent?: () => boolean;
}

function assertCurrent(options: CarryOverCommitOptions): void {
  if (options.isCurrent && !options.isCurrent())
    throw new Error(
      "Your wallet connection changed while keeping your private keys. Reconnect and retry.",
    );
}

type StoredRecord =
  | { v: 1; mode: "new" }
  | { v: 1; mode: "carried"; iv: string; data: string };

/** Which signature seeds the keys for this connection. */
export type CarryOverDecision =
  /** The previous signature, unlocked from this device's record. */
  | { kind: "carried"; signature: Uint8Array }
  /** The new message's signature seeds the keys. */
  | { kind: "new" }
  /** The wallet must sign the previous message once on this device. */
  | { kind: "needs-previous" };

const STORAGE_PREFIX = "stealthos_wallet_carry_over_v1";
const HKDF_SALT = "stealthos-wallet-carry-over-v1";

const utf8 = (text: string) => new TextEncoder().encode(text);

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function storageKey({ scope, wallet }: CarryOverScope): string {
  return `${STORAGE_PREFIX}:${scope}:${wallet}`;
}

// Storage errors are replaced by this message: a browser's own error text may
// carry arbitrary values, and signatures must never reach logs or the screen.
const STORAGE_ERROR =
  "This device's browser storage is unavailable. Enable storage for this site and try again.";

function storage(): Storage {
  try {
    if (typeof window !== "undefined" && window.localStorage)
      return window.localStorage;
  } catch {
    // Some browsers throw on access to localStorage itself.
  }
  throw new Error(STORAGE_ERROR);
}

function readItem(key: string): string | null {
  const store = storage();
  try {
    return store.getItem(key);
  } catch {
    throw new Error(STORAGE_ERROR);
  }
}

function writeItem(key: string, value: string): void {
  const store = storage();
  try {
    store.setItem(key, value);
  } catch {
    throw new Error(STORAGE_ERROR);
  }
}

function readRecord(target: CarryOverScope): StoredRecord | null {
  const raw = readItem(storageKey(target));
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    if (record?.v !== 1) return null;
    if (record.mode === "new") return record;
    if (
      record.mode === "carried" &&
      typeof record.iv === "string" &&
      typeof record.data === "string"
    )
      return record;
  } catch {
    // An unreadable record is treated as absent.
  }
  return null;
}

/** Whether this wallet already has a legacy key-derivation record. */
export function hasStoredCarryOverWallet(target: CarryOverScope): boolean {
  if (readItem(storageKey(target)) !== null && !readRecord(target)) {
    throw new Error(
      "This device's saved private-wallet setup is unreadable. Restore your StealthOS recovery phrase before continuing.",
    );
  }
  return readRecord(target) !== null;
}

/** AES-GCM key for this wallet and scope, from the new message's signature.
 * HKDF with its own salt keeps it independent of the new signature's other
 * use as a key seed. */
async function wrappingKey(
  target: CarryOverScope,
  newSignature: Uint8Array,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(newSignature),
    "HKDF",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: toArrayBuffer(utf8(HKDF_SALT)),
      info: toArrayBuffer(utf8(`${target.scope}:${target.wallet}`)),
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const fromBase64 = (text: string) =>
  Uint8Array.from(atob(text), (char) => char.charCodeAt(0));

/**
 * Decides which signature seeds the keys, given the new message's signature.
 * `existingWallet` is the user's own "I had a StealthOS wallet before" choice;
 * `hasPreviousWalletData` reports this wallet's saved data on this device from
 * before the change (no record yet, but notes, history or a journal).
 */
export async function resolveCarryOver(
  target: CarryOverScope,
  newSignature: Uint8Array,
  {
    existingWallet = false,
    hasPreviousWalletData,
  }: { existingWallet?: boolean; hasPreviousWalletData: () => boolean },
): Promise<CarryOverDecision> {
  const record = readRecord(target);
  if (record?.mode === "carried") {
    try {
      const plaintext = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: toArrayBuffer(fromBase64(record.iv)),
          additionalData: toArrayBuffer(
            utf8(`${target.scope}:${target.wallet}`),
          ),
        },
        await wrappingKey(target, newSignature),
        toArrayBuffer(fromBase64(record.data)),
      );
      return { kind: "carried", signature: new Uint8Array(plaintext) };
    } catch {
      // The wallet signed the new message differently than when the record
      // was made; signing the previous message again rebuilds it.
      return { kind: "needs-previous" };
    }
  }
  if (existingWallet) return { kind: "needs-previous" };
  if (record?.mode === "new") return { kind: "new" };
  return hasPreviousWalletData() ? { kind: "needs-previous" } : { kind: "new" };
}

/** Stores the previous signature, encrypted under the new one. */
export async function storeCarriedSignature(
  target: CarryOverScope,
  newSignature: Uint8Array,
  previousSignature: Uint8Array,
  options: CarryOverCommitOptions = {},
): Promise<void> {
  assertCurrent(options);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: toArrayBuffer(iv),
      additionalData: toArrayBuffer(utf8(`${target.scope}:${target.wallet}`)),
    },
    await wrappingKey(target, newSignature),
    toArrayBuffer(previousSignature),
  );
  const record: StoredRecord = {
    v: 1,
    mode: "carried",
    iv: toBase64(iv),
    data: toBase64(new Uint8Array(data)),
  };
  assertCurrent(options);
  writeItem(storageKey(target), JSON.stringify(record));
}

/** Records that this wallet uses the new message's keys on this device, so
 * its later saved data is not mistaken for an earlier private wallet. */
export function rememberNewWallet(target: CarryOverScope): void {
  if (readRecord(target)) return;
  const record: StoredRecord = { v: 1, mode: "new" };
  writeItem(storageKey(target), JSON.stringify(record));
}

/**
 * The step that saves a decision on this device, run only once the
 * connection it belongs to has been verified: a cancelled or changed
 * connection saves nothing.
 */
export function carryOverCommit(
  target: CarryOverScope,
  decision: CarryOverDecision,
  newSignature: Uint8Array,
  previousSignature?: Uint8Array,
  options: CarryOverCommitOptions = {},
): () => Promise<void> {
  if (decision.kind === "carried") return async () => assertCurrent(options);
  if (decision.kind === "new")
    return async () => {
      assertCurrent(options);
      rememberNewWallet(target);
    };
  if (!previousSignature)
    throw new Error("The previous signature is required to keep the keys.");
  return () =>
    storeCarriedSignature(target, newSignature, previousSignature, options);
}

/**
 * Whether this device holds saved data for the wallet from before the change:
 * a localStorage key, other than carry-over records, that mentions the wallet
 * and matches the network's own key pattern.
 */
export function hasSavedWalletData(matches: (key: string) => boolean): boolean {
  try {
    const store = storage();
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key && !key.startsWith(STORAGE_PREFIX) && matches(key)) return true;
    }
  } catch {
    // Unreadable storage holds nothing this connection could use.
  }
  return false;
}
